import CitationListComponent from "./citations";
import ButtonListComponent from "./buttons";
import CarouselListComponent from "./carousel";
import {
  ButtonPayloadEnum,
  MessageContent,
  MessageEmit,
} from "@aichat/types/socket/widget.types";
import { UserConversationRoleEnum } from "@aichat/types/enums/index";
import LinkPreviewListComponent from "./linkPreview";
import { cn } from "@aichat/ui/lib/utils";
import { HandleSendComponentParams } from "@aichat/types/widget";
import MediaComponent from "./media";
import { useWebWidgetConfig } from "../../web-widget/use-web-widget-config";
import SystemMessageComponent from "./systemMessage";
import ProductCarouselListComponent from "./productCarousel";
import { AssistantAvatarWithBg } from "../../assistant-avatar";
import { Typography } from "../../typography";
import { lazy } from "react";
import AttachmentComponent from "./attachment";
import CollectFormNodeComponent from "./form-component";
import MessageContainer, { MessageContainerProps } from "./message-container";
import MarkdownRenderer from "../../markdown-renderer";
import FormResponse from "./form-response";

const WhatsappTemplateMessage = lazy(
  () => import("./whatsapp-template-message"),
);

type ChatBubbleProps = {
  message: MessageContent;
  role: UserConversationRoleEnum;
  conversationLabelName?: string;
  createdAt: string;
  index: number;
  isFirstSameRoleBubble?: boolean;
  isLastSameRoleBubble?: boolean;
  isMiddleSameRoleBubble?: boolean;
  type?: "portal-chat" | "widget-chat";
  shouldAnimate?: boolean;
  widgetConfig: MessageContainerProps["widgetConfig"];
  // eslint-disable-next-line no-unused-vars
  handleSendComponent?: (params: HandleSendComponentParams) => void;
  handleSendForm?: (
    // eslint-disable-next-line no-unused-vars
    data: MessageEmit["message"]["form_response"],
  ) => void;
  hideAvatarInTextBubble?: boolean;
};

function ChatBubble__V2(props: ChatBubbleProps) {
  if (props.message.meta?.isWhatsappTemplate === true)
    return <WhatsappTemplateChatBubble {...props} message={props.message} />;
  else {
    return <WebChatBubble {...props} message={props.message} />;
  }
}

function WebChatBubble({
  message,
  role,
  createdAt,
  index,
  isFirstSameRoleBubble,
  isLastSameRoleBubble,
  conversationLabelName,
  isMiddleSameRoleBubble,
  shouldAnimate,
  type = "widget-chat",
  handleSendComponent,
  handleSendForm,
  hideAvatarInTextBubble,
  widgetConfig,
}: ChatBubbleProps) {
  const { content, liveAgentUser } = message;
  const isCustomer = role === UserConversationRoleEnum.CUSTOMER;
  const textComponent = content.text ? (
    <MessageContainer
      widgetConfig={widgetConfig}
      hideAvatar={hideAvatarInTextBubble}
      liveChatAgent={liveAgentUser}
      role={role}
      createdAt={createdAt}
      conversationLabelName={conversationLabelName}
      isFirstSameRoleBubble={isFirstSameRoleBubble}
      isLastSameRoleBubble={isLastSameRoleBubble}
      isMiddleSameRoleBubble={isMiddleSameRoleBubble}
      type={type}
      shouldAnimate={shouldAnimate}
    >
      <MarkdownRenderer
        widgetConfig={widgetConfig}
        role={role}
        type={type}
        text={content.text}
      />
    </MessageContainer>
  ) : null;

  const buttons =
    content.buttons && content.buttons.length ? (
      <ButtonListComponent
        type={type}
        isUser={isCustomer}
        buttons={content.buttons}
        handleSendComponent={handleSendComponent}
      />
    ) : null;

  const citations =
    content.citations && content.citations ? (
      <CitationListComponent citations={content.citations} />
    ) : null;

  const carousel = content.carousel ? (
    <CarouselListComponent
      type={type}
      isUser={isCustomer}
      {...content.carousel}
    />
  ) : null;

  const linkPreviews =
    content.link_preview && content.link_preview.length ? (
      <LinkPreviewListComponent
        type={type}
        isUser={isCustomer}
        linkPreviews={content.link_preview}
        handleSendComponent={handleSendComponent}
      />
    ) : null;

  const productCarousels =
    content.product_carousel && content.product_carousel.length ? (
      <ProductCarouselListComponent
        type={type}
        isUser={isCustomer}
        productCarousels={content.product_carousel}
        buttonLabel={widgetConfig.productCarouselButtonLabel}
      />
    ) : null;

  const medias =
    content.medias && content.medias.length ? (
      <MediaComponent medias={content.medias} type={type} isUser={isCustomer} />
    ) : null;

  const systemMessage = content.system_message ? (
    <SystemMessageComponent message={content.system_message} />
  ) : null;

  const attachments =
    content.attachments && content.attachments.length ? (
      <MessageContainer
        widgetConfig={widgetConfig}
        hideAvatar={hideAvatarInTextBubble}
        liveChatAgent={liveAgentUser}
        role={role}
        createdAt={createdAt}
        conversationLabelName={conversationLabelName}
        isFirstSameRoleBubble={isFirstSameRoleBubble}
        isLastSameRoleBubble={isLastSameRoleBubble}
        isMiddleSameRoleBubble={isMiddleSameRoleBubble}
        type={type}
        shouldAnimate={shouldAnimate}
      >
        <AttachmentComponent
          showAvatar={!textComponent}
          attachments={content.attachments}
          isCustomer={isCustomer}
          type={type}
        />
      </MessageContainer>
    ) : null;

  const linkButtons =
    content.link_buttons && content.link_buttons.length ? (
      <ButtonListComponent
        type={type}
        isUser={isCustomer}
        buttons={content.link_buttons.map((item) => ({
          id: item.button_link,
          label: item.button_label,
          payload: {
            type: ButtonPayloadEnum.LINK,
            value: item.button_link,
          },
        }))}
        handleSendComponent={handleSendComponent}
      />
    ) : null;

  const formComponent = content.form_component ? (
    <CollectFormNodeComponent
      widgetConfig={widgetConfig}
      hideAvatar={hideAvatarInTextBubble}
      liveChatAgent={liveAgentUser}
      role={role}
      createdAt={createdAt}
      conversationLabelName={conversationLabelName}
      isFirstSameRoleBubble={isFirstSameRoleBubble}
      isLastSameRoleBubble={isLastSameRoleBubble}
      isMiddleSameRoleBubble={isMiddleSameRoleBubble}
      type={type}
      shouldAnimate={shouldAnimate}
      handleSendForm={handleSendForm}
      config={content.form_component.config}
    />
  ) : null;

  const formResponse =
    Object.keys(content.form_response ?? {}).length > 0 ? (
      <div className="my-2">
        <MessageContainer
          widgetConfig={widgetConfig}
          hideAvatar={hideAvatarInTextBubble}
          liveChatAgent={liveAgentUser}
          role={role}
          createdAt={createdAt}
          conversationLabelName={conversationLabelName}
          isFirstSameRoleBubble={isFirstSameRoleBubble}
          isLastSameRoleBubble={isLastSameRoleBubble}
          isMiddleSameRoleBubble={isMiddleSameRoleBubble}
          type={type}
          shouldAnimate={shouldAnimate}
        >
          {content.form_response && (
            <FormResponse
              widgetConfig={widgetConfig}
              role={role}
              formResponse={content.form_response}
            />
          )}
        </MessageContainer>
      </div>
    ) : null;

  const config = useWebWidgetConfig();

  return (
    <div
      className={cn(
        "flex flex-col",
        isFirstSameRoleBubble ? "mt-4 mb-[2px]" : "",
        isLastSameRoleBubble ? "mb-4 mt-[2px]" : "",
        isMiddleSameRoleBubble ? "my-[2px]" : "",
        isCustomer ? "animate-scale-up-right" : "animate-scale-up-left",
        config.isThemeSleek && index === 0 ? "mt-auto" : "",
      )}
      id={`ai-chat-bubble-${index}`}
    >
      {attachments && <div className="mb-2">{attachments}</div>}
      {textComponent}
      {systemMessage && <div className="my-2">{systemMessage}</div>}
      {medias}
      {buttons}
      {citations}
      {carousel}
      {linkButtons}
      {formComponent}
      {formResponse}
      <div
        className={cn(
          type === "portal-chat" ? "max-w-[calc(70%_+_40px)] ml-auto mr-6" : "",
        )}
      >
        {linkPreviews}
      </div>
      <div
        className={cn(
          type === "portal-chat"
            ? " w-[70%] ml-auto @container/product-carousel"
            : "",
        )}
      >
        {productCarousels}
      </div>
    </div>
  );
}

function WhatsappTemplateChatBubble(props: ChatBubbleProps) {
  const {
    message,
    isFirstSameRoleBubble,
    isLastSameRoleBubble,
    isMiddleSameRoleBubble,
  } = props;

  const { avatarType, avatarUrl, liveAgentAvatarType, liveAgentAvatarUrl } =
    useWebWidgetConfig();

  const { liveAgentUser } = message;

  return (
    <div className="flex justify-end rounded-xl gap-2 items-end my-2">
      <div
        className={cn(
          "max-w-[75%] min-w-[20%] rounded-xl flex flex-col gap-4 w-fit p-2 shadow-sm",
          isFirstSameRoleBubble ? "rounded-br-[4px]" : "",
          isMiddleSameRoleBubble ? "rounded-r-[4px]" : "",
          isLastSameRoleBubble ? "rounded-tr-[4px]" : "",
        )}
        style={{
          boxShadow: "0px 1px 10px -1px #0000000A,0px 1px 5px -1px #00000012",
        }}
      >
        <Typography variant="detailMedium" className="text-muted-foreground">
          This is a templated message in WhatsApp.
        </Typography>
        {message.meta?.isWhatsappTemplate === true && (
          <div className="w-[360px]">
            <WhatsappTemplateMessage message={message.content} />
          </div>
        )}
      </div>
      <div className="shrink-0">
        {isLastSameRoleBubble ? (
          <AssistantAvatarWithBg
            containerProps={{
              className: "size-6 rounded-lg",
            }}
            avatarType={liveAgentUser ? "live-agent" : "bot"}
            avatarProps={{
              avatarType: liveAgentUser ? "live-agent" : "bot",
              customUrl: liveAgentUser
                ? (liveAgentAvatarUrl ?? liveAgentUser.avatarUrl)
                : avatarUrl,
              customUrlClassName: "size-6",
              className: "size-4",
              variant: liveAgentUser ? liveAgentAvatarType : avatarType,
            }}
          />
        ) : (
          <div className="size-6" />
        )}
      </div>
    </div>
  );
}

export default ChatBubble__V2;
